
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import { Issue } from '../types';

const TemplateArchive: React.FC = () => {
    const [archive, setArchive] = useState<Issue[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchArchive = async () => {
            try {
                const data = await VanityAPI.getArchive();
                // SORTING: Descending by Issue Number (issueDate holds the number like "145")
                // If issueDate is not a number, fallback to creation date
                const sorted = data.sort((a, b) => {
                    const issueA = parseInt(a.issueDate || '0', 10);
                    const issueB = parseInt(b.issueDate || '0', 10);
                    if (issueA && issueB) return issueB - issueA;
                    // Fallback to year
                    return (b.year || 0) - (a.year || 0);
                });
                // Filter only published (Active) issues
                setArchive(sorted.filter(m => m.status === 'published'));
            } catch (err) {
                console.error("Failed to load archive:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchArchive();
    }, []);

    if (loading) return null;

    // Flattened newest-first grid
    return (
        <div className="max-w-[1440px] mx-auto px-8 md:px-12 py-20 animate-fadeIn">
            <header className="mb-24 text-center max-w-4xl mx-auto space-y-8">
                <span className="font-sans text-[12px] uppercase tracking-[0.8em] text-vanity-pink font-extrabold">The Print Library</span>
                <h2 className="font-serif text-5xl md:text-7xl text-vanity-black leading-tight font-bold italic">Legacy of Excellence.</h2>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-24 lg:gap-32">
                {archive.map((issue) => (
                    <Link key={issue.id} to={`/archive/${issue.slug || issue.id}`} className="group block">
                        <div className="relative overflow-hidden mb-12 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.15)] bg-vanity-charcoal/5 aspect-[3/4] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:shadow-[0_50px_100px_-20px_rgba(214,0,109,0.15)] group-hover:-translate-y-2">
                            <img
                                src={issue.coverUrl}
                                alt={`${issue.season || ''} Edition`}
                                className="w-full h-full object-cover transition-all duration-[1.8s] ease-[cubic-bezier(0.16,1,0.3,1)] scale-100 group-hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-vanity-black/15 transition-opacity duration-1000 group-hover:opacity-0"></div>
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                                <div className="border border-white/50 bg-white/10 backdrop-blur-md px-6 py-3 text-white font-sans text-[10px] uppercase tracking-[0.3em] font-bold">View Anthology</div>
                            </div>
                        </div>
                        <div className="space-y-4 text-center md:text-left">
                            <h3 className="font-serif text-4xl text-vanity-black group-hover:text-vanity-pink transition-colors duration-500 font-bold">
                                {issue.title}
                            </h3>
                            <p className="font-sans text-[11px] uppercase tracking-[0.4em] text-vanity-charcoal font-extrabold opacity-70">
                                {/* FORMAT: Issue / Date (or Year) */}
                                {issue.issueDate} / {issue.periodText || issue.year}
                            </p>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default TemplateArchive;
